import { getColumnId } from 'components/TableSettings';
import type {
    WorkOrderChangeResultData,
    WorkOrderChangeSeasonData,
    WorkOrderChangeSeasonResultData,
} from 'gql/graphql';
import { useSelection } from 'hooks/useSelection';
import type { TableColumn } from 'uikit';
import { getNumberCell } from 'utils/tableCells';

import { useCallback, useMemo } from 'react';

import { getSeasonAccessor } from '../../../WorkOrder/WorkOrderSummaryTable/helpers';

const dataColumnWidth = 121;

interface IUseWorkOrderChangeSummaryColumns {
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    workOrderChangeId: number;
    data: WorkOrderChangeResultData[];
    costRounding: number;
}

export const useWorkOrderChangeSummaryColumns = (props: IUseWorkOrderChangeSummaryColumns) => {
    const { data, workOrderChangeSeasons, workOrderChangeId, costRounding } = props;
    const [selection, , , setSelection] = useSelection(`change_summary_${workOrderChangeId}`);

    const seasons: WorkOrderChangeSeasonData[] = useMemo(() => {
        const seasonsIds = data?.reduce((acc: number[], s) => {
            s?.seasonResults?.forEach(
                (r: WorkOrderChangeSeasonResultData) => !acc.includes(r.seasonId) && acc.push(r.seasonId),
            );

            return acc;
        }, []);

        return workOrderChangeSeasons.filter((season) => seasonsIds?.includes(season.id)) || [];
    }, [data, workOrderChangeSeasons]);

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const columns: TableColumn<any>[] = useMemo(
        () => [
            { title: 'Наименование', accessor: 'title', width: 466 },
            getNumberCell({
                title: 'План по нз',
                accessor: 'totalPlanCost',
                width: dataColumnWidth,
                align: 'right',
                decimal: costRounding,
            }),
            getNumberCell({
                title: 'Выполнено по нз',
                accessor: 'totalCompletedCost',
                width: dataColumnWidth,
                align: 'right',
                decimal: costRounding,
            }),
            getNumberCell({
                title: 'Остаток по НЗ',
                accessor: 'totalRemainderCost',
                width: dataColumnWidth,
                align: 'right',
                decimal: costRounding,
            }),
            ...seasons.map((season) =>
                getNumberCell({
                    title: `Сезон ${season.year}, план`,
                    accessor: getSeasonAccessor(season.id),
                    width: dataColumnWidth,
                    align: 'right',
                    decimal: costRounding,
                }),
            ),
        ],
        [costRounding, seasons],
    );

    const onTableSettingsConfirm = useCallback(
        (visible) => {
            const hidden = columns.reduce(
                (acc, column) => {
                    acc[getColumnId(column)] = false;

                    return acc;
                },
                {} as Record<string, false>,
            );

            setSelection({ ...hidden, ...visible });
        },
        [columns, setSelection],
    );

    return { columns, seasons, onTableSettingsConfirm, isColumnVisible };
};
